import type { CSSProperties } from "react";
import type { SiteBuilderBlock } from "./siteBuilderTypes";

export type SiteBuilderHoverEffect =
    | "blur"
    | "brighten"
    | "darken"
    | "glow"
    | "lift"
    | "lift-soft"
    | "outline"
    | "rotate"
    | "scale"
    | "scale-sm"
    | "shadow"
    | "skew"
    | "slide-right"
    | "underline";

export type SiteBuilderInteractionAttributes = {
    "data-builder-animation"?: string;
    "data-builder-animation-mode"?: "appearance" | "scroll";
    "data-builder-animation-trigger"?: "always" | "once";
    "data-builder-hover-colors"?: "true";
    "data-builder-hover-effect"?: SiteBuilderHoverEffect;
    "data-builder-item-hover-effect"?: SiteBuilderHoverEffect;
};

type SiteBuilderInteractionStyle = CSSProperties & Record<`--builder-${string}`, string>;

const hoverEffects: ReadonlySet<SiteBuilderHoverEffect> = new Set([
    "blur",
    "brighten",
    "darken",
    "glow",
    "lift",
    "lift-soft",
    "outline",
    "rotate",
    "scale",
    "scale-sm",
    "shadow",
    "skew",
    "slide-right",
    "underline",
]);

const animationNames = new Map<string, string>([
    ["blur-in", "siteBuilderBlurIn"],
    ["bounce-in", "siteBuilderBounceIn"],
    ["clip-down", "siteBuilderClipDown"],
    ["clip-left", "siteBuilderClipLeft"],
    ["clip-right", "siteBuilderClipRight"],
    ["clip-up", "siteBuilderClipUp"],
    ["fade-down", "siteBuilderFadeDown"],
    ["fade-in", "siteBuilderFadeIn"],
    ["fade-left", "siteBuilderFadeLeft"],
    ["fade-right", "siteBuilderFadeRight"],
    ["fade-up", "siteBuilderFadeUp"],
    ["flip-x", "siteBuilderFlipX"],
    ["flip-y", "siteBuilderFlipY"],
    ["float-in", "siteBuilderFloatIn"],
    ["reveal-down", "siteBuilderRevealDown"],
    ["reveal-left", "siteBuilderRevealLeft"],
    ["reveal-right", "siteBuilderRevealRight"],
    ["reveal-up", "siteBuilderRevealUp"],
    ["rotate-in", "siteBuilderRotateIn"],
    ["zoom-in", "siteBuilderZoomIn"],
    ["zoom-out", "siteBuilderZoomOut"],
    // Existing Web snapshot aliases.
    ["reveal", "siteBuilderFadeUp"],
    ["rise", "siteBuilderFadeUp"],
]);

const animationEffects = new Set([
    "fade",
    "zoom",
    "flip",
    "rotate",
    "blur",
    "bounce",
    "float",
    "clip",
    "reveal",
]);

const animationDirections = new Set([
    "",
    "up",
    "down",
    "left",
    "right",
    "x",
    "y",
    "in",
    "out",
]);

function optionText(block: SiteBuilderBlock, key: string): string {
    const value = block.options?.[key];

    return typeof value === "string" ? value.trim() : "";
}

function normalizedHoverEffect(value: string): SiteBuilderHoverEffect | "" {
    return hoverEffects.has(value as SiteBuilderHoverEffect)
        ? value as SiteBuilderHoverEffect
        : "";
}

function cssTimeValue(value: string): string {
    if (value === "") {
        return "";
    }

    if (/^\d+(\.\d+)?$/.test(value)) {
        return `${value}ms`;
    }

    return /^\d+(\.\d+)?m?s$/.test(value) ? value : "";
}

function cssPercentValue(value: string): string {
    if (value === "") {
        return "";
    }

    const numeric = Number(value.endsWith("%") ? value.slice(0, -1) : value);

    return Number.isFinite(numeric)
        ? `${Math.max(0, Math.min(100, numeric))}%`
        : "";
}

function animationFromEffect(effect: string, direction: string): string {
    if (effect === "fade") {
        if (direction === "up") return "fade-up";
        if (direction === "down") return "fade-down";
        if (direction === "left") return "fade-left";
        if (direction === "right") return "fade-right";

        return "fade-in";
    }

    if (effect === "zoom") {
        return direction === "out" ? "zoom-out" : "zoom-in";
    }

    if (effect === "flip") {
        return direction === "y" ? "flip-y" : "flip-x";
    }

    if (effect === "clip") {
        if (direction === "down") return "clip-down";
        if (direction === "left") return "clip-left";
        if (direction === "right") return "clip-right";

        return "clip-up";
    }

    if (effect === "reveal") {
        if (direction === "up") return "reveal-up";
        if (direction === "down") return "reveal-down";
        if (direction === "right") return "reveal-right";

        return "reveal-left";
    }

    if (effect === "blur") return "blur-in";
    if (effect === "bounce") return "bounce-in";
    if (effect === "float") return "float-in";

    return "rotate-in";
}

function resolvedAnimation(block: SiteBuilderBlock): {
    animation: string;
    mode: "appearance" | "scroll";
    trigger: "always" | "once";
} {
    const legacyAnimation = optionText(block, "animation");
    const rawMode = optionText(block, "animationMode");
    const mode = rawMode === "scroll" ? "scroll" : "appearance";
    const effectValue = optionText(block, "animationEffect");
    const directionValue = optionText(block, "animationDirection");
    const effect = animationEffects.has(effectValue) ? effectValue : "fade";
    const direction = animationDirections.has(directionValue)
        ? directionValue
        : "";
    const explicitAnimation = rawMode === "appearance" || rawMode === "scroll"
        ? animationFromEffect(effect, direction)
        : "";

    return {
        animation: explicitAnimation || legacyAnimation,
        mode,
        trigger: optionText(block, "animationTrigger") === "always"
            ? "always"
            : "once",
    };
}

function hoverEffectStyle(
    effect: SiteBuilderHoverEffect,
    style: SiteBuilderInteractionStyle,
): void {
    if (effect === "lift") {
        style["--builder-hover-transform"] = "translateY(-8px)";
        style["--builder-hover-shadow"] = "0 22px 55px var(--site-shadow-color, rgb(15 23 42 / .18))";
    } else if (effect === "lift-soft") {
        style["--builder-hover-transform"] = "translateY(-4px)";
        style["--builder-hover-shadow"] = "0 14px 34px var(--site-shadow-color, rgb(15 23 42 / .16))";
    } else if (effect === "scale") {
        style["--builder-hover-transform"] = "scale(1.045)";
    } else if (effect === "scale-sm") {
        style["--builder-hover-transform"] = "scale(1.02)";
    } else if (effect === "shadow") {
        style["--builder-hover-shadow"] = "0 18px 44px var(--site-shadow-color, rgb(15 23 42 / .18))";
    } else if (effect === "glow") {
        style["--builder-hover-shadow"] = "0 0 0 1px color-mix(in srgb, var(--site-primary) 28%, transparent), 0 0 34px color-mix(in srgb, var(--site-primary) 26%, transparent)";
    } else if (effect === "outline") {
        style["--builder-hover-border-color"] = "var(--site-primary)";
        style["--builder-hover-shadow"] = "0 0 0 2px color-mix(in srgb, var(--site-primary) 28%, transparent)";
    } else if (effect === "underline") {
        style["--builder-hover-underline"] = "1";
    } else if (effect === "slide-right") {
        style["--builder-hover-transform"] = "translateX(8px)";
    } else if (effect === "rotate") {
        style["--builder-hover-transform"] = "rotate(1.6deg)";
    } else if (effect === "skew") {
        style["--builder-hover-transform"] = "skewX(-2deg)";
    } else if (effect === "brighten") {
        style["--builder-hover-filter"] = "brightness(1.08)";
    } else if (effect === "darken") {
        style["--builder-hover-filter"] = "brightness(0.88)";
    } else if (effect === "blur") {
        style["--builder-hover-filter"] = "blur(1.25px)";
    }
}

export function siteBuilderInteractionAttributes(
    block: SiteBuilderBlock,
): SiteBuilderInteractionAttributes {
    const hoverEffect = normalizedHoverEffect(optionText(block, "hoverEffect"));
    const itemHoverEffect = normalizedHoverEffect(optionText(block, "itemHoverEffect"));
    const { animation, mode, trigger } = resolvedAnimation(block);
    const hasHoverColors = Boolean(
        optionText(block, "hoverTextColor")
        || optionText(block, "hoverBackgroundColor")
        || optionText(block, "hoverBorderColor"),
    );

    return {
        ...(hoverEffect ? { "data-builder-hover-effect": hoverEffect } : {}),
        ...(itemHoverEffect ? { "data-builder-item-hover-effect": itemHoverEffect } : {}),
        ...(hasHoverColors ? { "data-builder-hover-colors": "true" as const } : {}),
        ...(animationNames.has(animation) ? {
            "data-builder-animation": animation,
            "data-builder-animation-mode": mode,
            "data-builder-animation-trigger": trigger,
        } : {}),
    };
}

export function siteBuilderInteractionStyle(
    block: SiteBuilderBlock,
): CSSProperties | undefined {
    const hoverEffect = normalizedHoverEffect(optionText(block, "hoverEffect"));
    const itemHoverEffect = normalizedHoverEffect(optionText(block, "itemHoverEffect"));
    const hoverDuration = cssTimeValue(optionText(block, "hoverDuration"));
    const hoverTextColor = optionText(block, "hoverTextColor");
    const hoverBackgroundColor = optionText(block, "hoverBackgroundColor");
    const hoverBorderColor = optionText(block, "hoverBorderColor");
    const { animation, mode, trigger } = resolvedAnimation(block);
    const animationName = animationNames.get(animation);
    const animationDuration = cssTimeValue(optionText(block, "animationDuration"));
    const animationDelay = cssTimeValue(
        optionText(block, "animationStartAfter")
        || optionText(block, "animationDelay"),
    );
    const animationScrollStart = cssPercentValue(
        optionText(block, "animationScrollStart"),
    );
    const animationScrollEnd = cssPercentValue(
        optionText(block, "animationScrollEnd"),
    );
    const style: SiteBuilderInteractionStyle = {};

    if (hoverEffect || hoverTextColor || hoverBackgroundColor || hoverBorderColor) {
        style["--builder-hover-has-options"] = "1";
    }

    if (hoverDuration) {
        style["--builder-hover-duration"] = hoverDuration;
    }

    if (hoverEffect) {
        hoverEffectStyle(hoverEffect, style);
    }

    if (itemHoverEffect) {
        hoverEffectStyle(itemHoverEffect, style);
    }

    if (hoverTextColor) {
        style["--builder-hover-text-color"] = hoverTextColor;
    }

    if (hoverBackgroundColor) {
        style["--builder-hover-background-color"] = hoverBackgroundColor;
    }

    if (hoverBorderColor) {
        style["--builder-hover-border-color"] = hoverBorderColor;
    }

    if (animationName) {
        style["--builder-animation-name"] = animationName;
        style["--builder-animation-mode"] = mode;
        style["--builder-animation-trigger"] = trigger;
        style["--builder-animation-duration"] = animationDuration || "700ms";
        style["--builder-animation-delay"] = animationDelay || "0ms";
        style["--builder-animation-scroll-start"] = animationScrollStart || "0%";
        style["--builder-animation-scroll-end"] = animationScrollEnd || "60%";
    }

    return Object.keys(style).length > 0 ? style : undefined;
}

export const SITE_BUILDER_INTERACTION_CSS = `
:where(
    [data-builder-hover-effect],
    [data-builder-hover-colors],
    [data-builder-item-hover-effect],
    [style*="--builder-hover-has-options"]
) {
    transition-property: transform, box-shadow, filter, color, background-color, border-color, text-decoration-color;
    transition-duration: var(--builder-hover-duration, 180ms);
    transition-timing-function: cubic-bezier(0.2, 0.8, 0.2, 1);
}

[style*="--builder-hover-transform"]:is(:hover, :focus-visible, :focus-within) {
    transform: var(--builder-hover-transform) !important;
}

[style*="--builder-hover-shadow"]:is(:hover, :focus-visible, :focus-within) {
    box-shadow: var(--builder-hover-shadow) !important;
}

[style*="--builder-hover-filter"]:is(:hover, :focus-visible, :focus-within) {
    filter: var(--builder-hover-filter) !important;
}

[style*="--builder-hover-text-color"]:is(:hover, :focus-visible, :focus-within) {
    color: var(--builder-hover-text-color) !important;
}

[style*="--builder-hover-background-color"]:is(:hover, :focus-visible, :focus-within) {
    background-color: var(--builder-hover-background-color) !important;
}

[style*="--builder-hover-border-color"]:is(:hover, :focus-visible, :focus-within) {
    border-color: var(--builder-hover-border-color) !important;
}

[style*="--builder-hover-underline"]:is(:hover, :focus-visible, :focus-within) {
    text-decoration-line: underline;
    text-decoration-thickness: 2px;
    text-underline-offset: 0.22em;
}

:where([data-builder-animation], [style*="--builder-animation-name"]) {
    animation-delay: var(--builder-animation-delay, 0ms);
    animation-duration: var(--builder-animation-duration, 700ms);
    animation-fill-mode: both;
    animation-name: none;
    animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
}

:where(
    [data-builder-animation-active="true"],
    [style*="--builder-animation-name"][data-builder-animation-active="true"]
) {
    animation-name: var(--builder-animation-name);
}

:where(
    [data-builder-animation-mode="scroll"],
    [style*="--builder-animation-mode"][data-builder-animation-mode="scroll"]
) {
    animation-delay: 0ms;
    animation-timeline: view();
    animation-range:
        entry var(--builder-animation-scroll-start, 0%)
        cover var(--builder-animation-scroll-end, 60%);
}

.site-builder-social-link.is-link-underlineOnHover:hover,
.site-builder-social-link.is-link-underlineOnHover:focus-visible {
    text-decoration-line: underline !important;
    text-decoration-thickness: 2px;
    text-underline-offset: 0.22em;
}

.site-builder-accordion-item > summary {
    align-items: center;
    cursor: pointer;
    display: flex;
    gap: .75rem;
    justify-content: space-between;
}

.site-builder-accordion-icon-open {
    display: none;
}

.site-builder-accordion-item[open] > summary .site-builder-accordion-icon-closed {
    display: none;
}

.site-builder-accordion-item[open] > summary .site-builder-accordion-icon-open {
    display: inline-flex;
}

.site-builder-accordion-item.is-style-boxed {
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius, 12px);
    padding: .8rem 1rem;
}

.site-builder-accordion-item.is-style-separated {
    border-bottom: 1px solid var(--site-border);
    border-radius: 0;
    padding: .8rem 0;
}

.site-builder-accordion-item.is-style-highlightActive[open] {
    background: color-mix(in srgb, var(--site-primary) 9%, transparent);
}

.site-builder-accordion-item.is-transition-slide[open] > :not(summary) {
    animation: siteBuilderAccordionSlide 180ms ease-out both;
}

.site-builder-accordion-item.is-transition-fade[open] > :not(summary) {
    animation: siteBuilderAccordionFade 180ms ease-out both;
}

@keyframes siteBuilderAccordionSlide {
    from { opacity: 0; transform: translateY(-.45rem); }
    to { opacity: 1; transform: translateY(0); }
}

@keyframes siteBuilderAccordionFade {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes siteBuilderFadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes siteBuilderFadeUp {
    from { opacity: 0; transform: translate3d(0, 24px, 0); }
    to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes siteBuilderFadeDown {
    from { opacity: 0; transform: translate3d(0, -24px, 0); }
    to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes siteBuilderFadeLeft {
    from { opacity: 0; transform: translate3d(24px, 0, 0); }
    to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes siteBuilderFadeRight {
    from { opacity: 0; transform: translate3d(-24px, 0, 0); }
    to { opacity: 1; transform: translate3d(0, 0, 0); }
}

@keyframes siteBuilderZoomIn {
    from { opacity: 0; transform: scale(.92); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes siteBuilderZoomOut {
    from { opacity: 0; transform: scale(1.08); }
    to { opacity: 1; transform: scale(1); }
}

@keyframes siteBuilderFlipX {
    from { opacity: 0; transform: perspective(800px) rotateX(-18deg); }
    to { opacity: 1; transform: perspective(800px) rotateX(0); }
}

@keyframes siteBuilderFlipY {
    from { opacity: 0; transform: perspective(800px) rotateY(-18deg); }
    to { opacity: 1; transform: perspective(800px) rotateY(0); }
}

@keyframes siteBuilderRotateIn {
    from { opacity: 0; transform: rotate(-4deg) scale(.96); }
    to { opacity: 1; transform: rotate(0) scale(1); }
}

@keyframes siteBuilderBlurIn {
    from { filter: blur(12px); opacity: 0; }
    to { filter: blur(0); opacity: 1; }
}

@keyframes siteBuilderBounceIn {
    0% { opacity: 0; transform: scale(.88); }
    65% { opacity: 1; transform: scale(1.035); }
    100% { opacity: 1; transform: scale(1); }
}

@keyframes siteBuilderFloatIn {
    from { opacity: 0; transform: translate3d(0, 30px, 0) scale(.98); }
    to { opacity: 1; transform: translate3d(0, 0, 0) scale(1); }
}

@keyframes siteBuilderClipUp {
    from { clip-path: inset(100% 0 0 0); opacity: 0; }
    to { clip-path: inset(0); opacity: 1; }
}

@keyframes siteBuilderClipDown {
    from { clip-path: inset(0 0 100% 0); opacity: 0; }
    to { clip-path: inset(0); opacity: 1; }
}

@keyframes siteBuilderClipLeft {
    from { clip-path: inset(0 0 0 100%); opacity: 0; }
    to { clip-path: inset(0); opacity: 1; }
}

@keyframes siteBuilderClipRight {
    from { clip-path: inset(0 100% 0 0); opacity: 0; }
    to { clip-path: inset(0); opacity: 1; }
}

@keyframes siteBuilderRevealUp {
    from { clip-path: inset(100% 0 0); transform: translate3d(0, 18px, 0); }
    to { clip-path: inset(0); transform: translate3d(0, 0, 0); }
}

@keyframes siteBuilderRevealDown {
    from { clip-path: inset(0 0 100%); transform: translate3d(0, -18px, 0); }
    to { clip-path: inset(0); transform: translate3d(0, 0, 0); }
}

@keyframes siteBuilderRevealLeft {
    from { clip-path: inset(0 0 0 100%); transform: translate3d(18px, 0, 0); }
    to { clip-path: inset(0); transform: translate3d(0, 0, 0); }
}

@keyframes siteBuilderRevealRight {
    from { clip-path: inset(0 100% 0 0); transform: translate3d(-18px, 0, 0); }
    to { clip-path: inset(0); transform: translate3d(0, 0, 0); }
}

@media (prefers-reduced-motion: reduce) {
    :where([data-builder-animation], [style*="--builder-animation-name"]) {
        animation: none !important;
    }

    :where(
        [data-builder-hover-effect],
        [data-builder-hover-colors],
        [data-builder-item-hover-effect]
    ) {
        transition-duration: 0ms !important;
    }
}
`;
